import React, { useEffect, useState } from 'react';
import useStyles from './DocumentChecklist.styles';
import { get, first, size, set, sortBy, isEmpty } from 'global/lib/lodash';
import { EmptySquareIcon } from 'global/components/PerformIcons';
import clsx from 'clsx';
import sStyles from 'global/styles';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { sortAlphaNum } from 'global/utils';
import RatingCheck from './RatingChecklist';
import useBodyOverflow from 'global/hooks/useBodyOverflow';
import useRetrieveData from 'global/hooks/useRetrieveData';
import Styles, { CenterLoader, Loader, useScreen } from 'global/styles';
import Image, { ImageList } from 'global/components/image';
import StepByStep from '../StepByStep';
import { SelectLangEditBar } from 'global/components/lang';
import { useLocation } from 'react-router-dom';
import { useShowChanges } from 'global/hooks/useShowChanges';
import { getUrlParams } from 'global/utils';
import { Button } from 'global/components/Button';
import { Box, Icon } from '@mui/material';
import colors from 'global/styles/colors';
import Form from 'global/components/form';
import setFilter from 'redux/actions/setFilter';
import { awaitAll } from 'global/utils/awaitAll';
import sendData from 'redux/actions/sendData';
import { UploadFiles } from 'redux/actions/imageUploader';
import { Column, Row } from 'global/components/row';
import { Text } from 'global/components/text';
import { cleanCopy } from 'global/utils';
import { FILTERDEFAULTS } from 'global/components/filterchips/definitions';
import { useEdit } from 'global/hooks/useEdit';
import Modal, { FullScreen } from 'global/components/modal';
import { CreateChecklist } from '../../../admin/content/create-checklist';
import CheckpointInfoModal from 'global/components/CheckpointInfo';
import { getIcon } from '../../definitions';
import { Media } from 'global/components/Media';
import DiffViewer from 'global/components/diff-text';
import { useBuilders } from 'global/hooks/useBuilders';

const DocumentChecklist = (props) => {
  const { doc, parentDocument, edition } = props;
  const checklist = get(doc, 'document', {});
  const classes = Styles();
  const userBuilderAccess = useBuilders().map((b) => b.id);
  const canShowChanges = !!get(edition, 'parentId') && userBuilderAccess.includes(get(edition, 'ownerRealmId').toString());

  const isSBS = doc.documentableType === 'step_by_step';

  const {
    data,
    loading,
    update: updatedocs,
  } = useRetrieveData(
    {
      url: isSBS ? `/step-by-steps/${checklist.id}` : `/checklists/${checklist.id}`,
    },
    {
      useEffect: true,
      useLangFilter: true,
      build: isSBS ? ['stepBySteps'] : ['checklists'],
      params: {
        include: isSBS ? 'items,images,items.images' : 'checkpoints.checkpointOptions,checkpoints.images,checkpoints.comments,classification.system,tradeTypes',
        editMode: 1,
      },
      transformData: (d) => first(d),
    }
  );

  const update = async () => {
    await Promise.all([await updatedocs(), await get(props, 'update', () => 0)(), await get(window, 'DOC_LIST_EDITION.update', () => 0)()]);
  };

  const showTranslations = size(get(data, 'translations')) > 1;

  const component = () => {
    if (loading) {
      return (
        <div className={classes.processingContainer}>
          <Loader />
        </div>
      );
    } else if (isSBS) {
      return <StepByStep {...props} data={data} />;
    } else {
      return <DocumentChecklistRender {...props} update={update} parentChecklist={parentDocument} checklist={data} isSBS={isSBS} />;
    }
  };

  return (
    <>
      {!props.printView && !props.isCourse && <SelectLangEditBar edition={edition} showTranslations={showTranslations} canEdit={props.userCanEdit} canShowChanges={canShowChanges} />}
      {component()}
    </>
  );
};

const DocumentChecklistRender = (props) => {
  const loca = useLocation();
  const { checklist, parentChecklist, documentParentId, userCanEdit, doc } = props;
  const Styles = sStyles();
  const classes = useStyles();
  // const placeimage = 'https://via.placeholder.com/350x550';
  const image = get(checklist, 'image', false);
  const checkpoints = get(checklist, 'checkpoints', []);
  const edition = props.edition;
  const view = useSelector((state) => get(state, 'view'));
  const [editMode, setEditMode] = useState(false);
  const [formState, setFormState] = useState({});
  const dispatch = useDispatch();
  const store = useStore();
  const [loading, setLoading] = useState(false);
  const { editOn } = useEdit();
  const [checkpointInfo, setCheckpointInfo] = useState({});
  const { showChangesOn } = useShowChanges();
  const [needsUpdate, setNeedsUpdate] = useState(false);

  const showChanges = showChangesOn && !!documentParentId && !parentChecklist?.loading;

  const [editProps, setEditProps] = useState({
    open: false,
    onClose: (updated) => {
      setEditProps((s) => ({ ...s, open: false }));
      if (updated) {
        // update();
        // edDocs.update();
      }
    },
  });

  const [confirmProps, setconfirmProps] = useState({
    open: false,
    onClose: (updated) => {
      setconfirmProps((s) => ({ onClose: s.onClose, open: false }));
    },
  });

  const editVals = get(doc, 'document.editMode', {});
  const { owned, editionUnscored, unique, unscored } = editVals;

  const canShowEdit = userCanEdit;
  // Criteria: Checkpoint descriptions & options can't be modified if a checklist is in a scorecard in the Edition it's being observed in (i.e. `editionUnscored`)
  const canEditCriteria = editionUnscored;
  // Incidentals: Other checkpoint data (Translations, Media, and Notes) editing is more permissive, but is restricted to only content that the user's company owns entirely (regardless of scorecard-inclusion)
  const canEditIncidentals = owned;
  // In places where content is shared content that _can_ have a new version created -- i.e. "Unscored" content,
  //    the user must create a new version first to then edit the incidentals.
  // If the content is both scored and not owned, the user has to create a new Edition just like for editing criteria.
  const canCreateNewVersion = !owned && editionUnscored;
  const scorecard = !canEditCriteria ? get(props, 'edition.scorecards', []).find((sc) => sc.checklists.find((cl) => cl.id == checklist.id)) : null;

  const onEditCheck = (opt) => {
    const showConfirm = unscored && owned && !unique;

    const goEdit = (opts = {}) => {
      confirmProps.onClose();

      setEditProps((s) => ({
        ...s,
        open: true,
        Component: () => (
          <CreateChecklist
            checklist={checklist}
            doc={{ ...get(props, 'edition', {}), editionDocuments: get(props, 'documents', []) }}
            editionId={get(props, 'edition.id')}
            onClose={props.update}
            {...opts}
          />
        ),
        title: `Edit Checklist`,
      }));
    };

    if (showConfirm) {
      setconfirmProps((s) => ({
        ...s,
        open: true,
        title: 'THIS CHECKLIST IS NOT UNIQUE',
        content: (
          <div>
            <Text>This checklist you are editing appears in multiple places on your account. Would you like to edit all of them or create a new version here?</Text>
          </div>
        ),
        buttons: [
          {
            title: 'CREATE NEW VERSION',
            variant: 'outlined',
            icon: <Icon>add</Icon>,
            onClick: () => goEdit({ createNew: true }),
          },
          { title: 'EDIT ALL INSTANCES', icon: <Icon>edit</Icon>, onClick: () => goEdit({ update_non_unique: true }) },
        ],
      }));
    } else {
      goEdit();
    }
  };

  const { isUp } = useScreen();

  const showLogo = get(props, 'showLogo', false);

  const params = getUrlParams(loca.search);

  useBodyOverflow({ hidden: isUp && !props.printView });

  // Find all checkpoint option values that have text -- this is how many columns to render
  const optionVals = checkpoints.reduce(
    (acc, c) =>
      get(c, 'checkpointOptions', []).reduce((ac, co) => {
        if (get(co, 'name') && get(co, 'name', '').trim() !== '' && ac.indexOf(get(co, 'value')) === -1) {
          ac = [get(co, 'value'), ...ac];
        }
        return ac;
      }, acc),
    []
  );

  // TO-DO: This boolean doesn't capture all possible variations
  const isRating = optionVals.length > 1;

  const additionalText = get(props, 'additionalText');
  const subText = get(props, 'subText');

  const longTitle = checkpoints.filter((cp) => cp.name.length > 2).length > 0;

  const formUpdate = (fs, cp) => {
    const fstate = { ...formState };
    set(fstate, cp.id, fs);
    setFormState(fstate);
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      const edition_id = get(props, 'edition.id');
      const edition_document_id = get(props, 'doc.id', []);

      await awaitAll(
        checkpoints.map((cp) => () => async () => {
          const images = get(formState, [cp.id, 'images'], cp.images);
          const cop = { ...cp };
          const data = {
            data: {
              type: 'checkpoints',
              id: cop.id,
              attributes: {
                images: images,
                edition_id,
                edition_document_id,
              },
            },
          };
          await UploadFiles(data);
          await sendData({
            url: `/checkpoints/${cop.id}`,
            method: 'patch',
            data,
          });
        }),
        { queueSize: 2 }
      );

      const mainImages = get(formState, 'main.images', []);
      if (!!size(mainImages)) {
        const cop = { ...checklist };
        const data = {
          data: {
            type: 'checklists',
            id: cop.id,
            attributes: {
              images: mainImages,
              edition_id,
              edition_document_id,
            },
          },
        };
        await UploadFiles(data);
        await sendData({
          url: `/checklists/${cop.id}`,
          method: 'patch',
          data,
        });
      }
      props.update && (await props.update());
    } catch (err) {
      console.error(err.message);
      setEditMode(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSetCheckpointInfo = (cp, checklist) => {
    setCheckpointInfo({
      open: true,
      checkpoint: { ...cp },
      checklist: checklist,
      scorecard: scorecard,
      onClose: () => {
        setCheckpointInfo({ open: false });
        setNeedsUpdate((prev) => {
          if (prev) {
            props.update && props.update();
          }
          return false;
        });
      },
    });
  };

  const renderForm = (cp, i) => {
    if (!editMode) {
      return null;
    }

    const isMain = cp.id === 'main';

    let currentFilters = {};

    return (
      <div className={clsx(classes.form, loading && classes.formLoading)}>
        <Form
          onFormState={(fs) => formUpdate(fs, cp)}
          showAllScreens={true}
          canSubmit={i === 0 && !loading}
          isNoMarginContainer={true}
          onSubmit={onSubmit}
          submitText={
            <>
              <Icon>save</Icon>
              <div style={{ marginLeft: 8 }}>Submit</div>
            </>
          }
          screens={[
            {
              title: '',
              inputs: [
                {
                  id: `images`,
                  type: 'DropArea',
                  defaultValue: () => (isMain ? (checklist.image ? [get(checklist, 'image')] : []) : sortBy(get(cp, 'images', []), 'orderColumn')),
                  props: {
                    accept: ['image/*'],
                    label: isMain ? 'Checklist Image (Only one allowed)' : undefined,
                    multiple: isMain ? false : true,
                    sortable: true,
                    confirmDelete: true,
                    shouldUpdateOnCaptionChange: true,
                    notAllowed: ['tag'],
                    collection: 'checklist-images',
                    uploadAllNew: true,
                    imageSelect: {
                      onOpen: () => {
                        currentFilters = cleanCopy(get(store.getState(), 'filter.reports.photo', {}));
                        const filt = {
                          ...get(FILTERDEFAULTS, 'reports.photo', {}),
                          searchstring: get(checklist, 'name'),
                          superset_id: get(checklist, 'classification.system.id'),
                        };
                        dispatch(setFilter('reports', 'photo', filt));
                      },
                      onClose: () => {
                        dispatch(setFilter('reports', 'photo', currentFilters));
                      },
                      useFullPhotos: true,
                      display: 'photo',
                    },
                  },
                },
              ],
            },
          ]}
        />
      </div>
    );
  };

  return (
    <div className={clsx(classes.container, props.className)}>
      <div className={!showLogo && Styles.printOnly}>
        <div className={Styles.printLogoContainer}>
          <Image style={{ height: 100 }} src={get(view, 'style.props.logo')} alt={'Builder Logo'} unsetWidth />
        </div>
        {additionalText && <div className={classes.additionalText}>{additionalText}</div>}
        <div className={classes.checklistName}>{get(checklist, 'name')}</div>
        {subText && <div className={classes.subText}>{subText}</div>}
      </div>

      {showChangesOn && !props.printView && (
        <Column className={clsx(classes.editButtonRow, classes.dangerBg)}>
          {<Text type="label">{`Showing changes between ${get(props, 'edition.name', 'Unknown')} and ${get(props, 'editionParent.name', 'Unknown')}`}</Text>}
        </Column>
      )}

      {canShowEdit && editOn && (
        <Column className={clsx(classes.editButtonRow, !canEditCriteria && classes.dangerBg)}>
          {!canEditCriteria && (
            <div style={{ marginBottom: 8 }}>
              <Text bold>{`This Checklist is in the ${get(scorecard, 'name', 'Unknown')} Scorecard`}</Text>
              <Text
                type="label_small"
                dangerouslySetInnerHTML={{
                  __html: `This Checklist is in a ${get(
                    props,
                    'edition.name',
                    'Unknown'
                  )} scorecard, which means it's referenced by Assessment data and can't be edited. If you would like to modify checkpoints or options, you must either delete the scorecard and all data, or create a <b>new Edition</b> version through the admin interface`,
                }}
              />
            </div>
          )}

          <Row>
            <Button className={classes.editButton} disabled={!canEditCriteria} startIcon={<Icon>{'edit'}</Icon>} onClick={() => onEditCheck()}>
              EDIT CRITERIA
            </Button>{' '}
            <Button
              className={classes.editButton}
              disabled={!canEditIncidentals}
              bgColor={editMode ? colors.danger : undefined}
              startIcon={<Icon>{editMode ? 'close' : 'collections'}</Icon>}
              onClick={() => setEditMode(!editMode)}
            >
              EDIT MEDIA
            </Button>
            {/* TODO Just submit the new checklist without any changes instead of opening the form */}
            {canCreateNewVersion && (
              <Button className={classes.editButton} startIcon={<Icon>{'collections'}</Icon>} onClick={() => onEditCheck()}>
                NEW VERSION
              </Button>
            )}
          </Row>
        </Column>
      )}

      {get(checklist, 'description') && (
        <Text variant="body2" className={classes.buffer}>
          {showChanges ? <DiffViewer oldText={get(parentChecklist, 'data.description', '')} newText={get(checklist, 'description', '')} diffType="words" /> : get(checklist, 'description', '')}
        </Text>
      )}

      {(image || (editMode && !isRating)) && (
        <div className={clsx(classes.chkimg)}>
          {image && !editMode && <Media sourceType={['large']} viewOnly {...image} />}
          {editMode && renderForm({ id: 'main' }, 1)}
        </div>
      )}

      {!!loading && (
        <Column center style={{ margin: 8 }}>
          <CenterLoader />
          <Text type="label_medium">Saving</Text>
        </Column>
      )}

      {isRating ? (
        <RatingCheck
          {...props}
          scorecard={scorecard}
          checklist={checklist}
          parentChecklist={get(parentChecklist, 'data')}
          showChanges={showChanges}
          editMode={editMode}
          renderForm={renderForm}
          optionVals={optionVals}
          needsUpdate={needsUpdate}
          setNeedsUpdate={setNeedsUpdate}
        />
      ) : (
        <div className={image || editMode ? classes.contentContainer : classes.contentContainerNoImage}>
          {checkpoints.sort(sortAlphaNum).map((cp, i) => {
            const desc = get(cp, 'description', '');
            const highlight = get(params, 'hl') === cp.id;
            // incidental data only includes notes and comments, because these don't display
            // images and translations aren't included, since they can display.
            const hasIncidentalData = cp.notes || (userCanEdit && size(get(cp, 'comments', [])) > 0);
            const icon = <Icon style={{ fontSize: 33 }}>{getIcon('checklist')}</Icon>;
            const images = cp.images.map((c) => ({ ...c, icon, description: `${cp.name}: ${desc}` }));
            const parentCheckpoint = showChanges ? get(parentChecklist, 'data.checkpoints', []).find((pcp) => pcp.id === cp.id || pcp.id === cp.checkpointId) || {} : undefined;
            return (
              <div key={cp.id} className={classes.buffer}>
                <div key={cp.id} className={clsx(classes.row, classes.centerVertically, highlight && classes.hlRow)}>
                  <div className={classes.iconContainer}>
                    <EmptySquareIcon />
                  </div>
                  <div className={classes.rowText}>
                    <strong className={!longTitle && classes.capitalize}>
                      {parentCheckpoint !== undefined ? <DiffViewer oldText={get(parentCheckpoint, 'name', '')} newText={cp.name} diffType="words" /> : cp.name}
                      {!longTitle && `.`}
                      {` `}
                    </strong>
                    {parentCheckpoint !== undefined ? (
                      <DiffViewer oldText={get(parentCheckpoint, 'description', '')} newText={desc} diffType="words" />
                    ) : (
                      <span dangerouslySetInnerHTML={{ __html: desc }} />
                    )}
                  </div>
                  {!props.printView && (hasIncidentalData || editOn) && (
                    <>
                      <div style={{ flexGrow: 1 }} />
                      <Button variant="text" color="secondary" onClick={() => handleSetCheckpointInfo(cp, checklist)}>
                        {hasIncidentalData ? (
                          <>
                            {get(cp, 'notes') && <Icon fontSize="small">text_snippet</Icon>}
                            {userCanEdit && size(get(cp, 'comments')) > 0 && <Icon fontSize="small">message</Icon>}
                          </>
                        ) : (
                          <Icon fontSize="small">info_outlined</Icon>
                        )}
                      </Button>
                    </>
                  )}
                </div>
                {cp.images && !props.printView && (
                  <Box displayPrint="none" className={clsx(classes.row, classes.imageRowSpacing)}>
                    <ImageList spaceBetween={3} images={images} />
                  </Box>
                )}
                {renderForm(cp, i)}
              </div>
            );
          })}
        </div>
      )}

      <FullScreen {...editProps}>{!!editProps.open && !!editProps.Component && <editProps.Component {...editProps} />}</FullScreen>
      <Modal {...confirmProps} />
      <CheckpointInfoModal {...checkpointInfo} edition={edition} needsUpdate={needsUpdate} setNeedsUpdate={setNeedsUpdate} />
    </div>
  );
};

export default DocumentChecklist;
